import React, { useMemo, useState } from "react";
import "./DetailedActivityStream.scss";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { confirmPopup } from "primereact/confirmpopup";
import { COMMENT_FIELD_LIMITS } from "../../Util/entityFieldLimits";
import { FieldValidationRules, isFieldValueValid } from "../../Util/fieldValidation";

export interface IDetailedActivityEvent {
    uuid: string;
    eventType: string;
    contentTypeIcon?: string;
    contentTypeDescription?: string;
    payload?: Record<string, any>;
    actorName?: string;
    createdBy?: string;
    createdAt?: string;
}

interface DetailedActivityStreamProps<T extends IDetailedActivityEvent = IDetailedActivityEvent> {
    title?: string;
    events: T[];
    loading?: boolean;
    emptyMessage?: string;
    commentPlaceholder?: string;
    commentValidation?: FieldValidationRules;
    maxHeight?: string;
    onAddComment?: (commentText: string) => Promise<void> | void;
    onDeleteComment?: (event: T) => Promise<void> | void;
    canEditComments?: boolean;
    currentUserUuid?: string;
    detailResolver?: (event: T) => string | null | undefined;
    eventLabelResolver?: (event: T) => string;
    collapsibleComposer?: boolean;
    addCommentButtonLabel?: string;
    showHeader?: boolean;
}

export const resolveDetailedActivityEventLabel = (eventType: string, translate: TFunction): string => {
    const labels: Record<string, string> = {
        created: "Aangemaakt",
        updated: "Bijgewerkt",
        task_added: "Taak toegevoegd",
        task_updated: "Taak bijgewerkt",
        task_assignee_change: "Taak toegewezen",
        comment: "Opmerking",
        comment_added: "Opmerking toegevoegd",
        message: "Opmerking",
        status_change: "Status gewijzigd",
        status_changed: "Status gewijzigd",
        assignee_change: "Assignee gewijzigd",
        step_note: "Stapnotitie",
        alert_triggered: "Alert getriggerd",
        email_sent: "Email verzonden",
        criteria_manual_input: translate("alert.activity.manualInput", { defaultValue: "Waarde handmatig ingevoerd" }),
        article_published: "Gepubliceerd",
        article_archived: "Gearchiveerd",
        article_featured: "Aanbevolen gemarkeerd",
        article_unfeatured: "Niet meer aanbevolen",
        article_updated: "Bijgewerkt",
        article_media_added: "Media toegevoegd",
        article_media_removed: "Media verwijderd",
    };
    return labels[eventType] || eventType;
};

const defaultIconClass = (event: IDetailedActivityEvent): string => {
    if (event.contentTypeIcon) return event.contentTypeIcon;
    const eventType = (event.eventType || "").toLowerCase();
    if (eventType === "comment" || eventType === "message") return "pi pi-comment";
    if (eventType === "comment_added") return "pi pi-comment";
    if (eventType === "email_sent") return "pi pi-envelope";
    if (eventType === "status_change" || eventType === "status_changed") return "pi pi-sync";
    if (eventType === "task_added" || eventType === "task_updated" || eventType === "task_assignee_change") return "pi pi-check-square";
    if (eventType === "alert_triggered") return "pi pi-bell";
    if (eventType === "criteria_manual_input") return "pi pi-pencil";
    if (eventType === "article_published") return "pi pi-eye";
    if (eventType === "article_archived") return "pi pi-eye-slash";
    if (eventType === "article_featured" || eventType === "article_unfeatured") return "pi pi-star";
    if (eventType === "article_updated") return "pi pi-pencil";
    if (eventType === "article_media_added" || eventType === "article_media_removed") return "pi pi-paperclip";
    return "pi pi-info-circle";
};

export const resolveDetailedActivityEventDetail = (event: IDetailedActivityEvent): string | null => {
    const payload = event.payload || {};
    const eventType = (event.eventType || "").toLowerCase();

    if (eventType === "criteria_manual_input") {
        const scopes = typeof payload.scopes === "string" ? payload.scopes.trim() : "";
        const value = payload.value != null ? `${payload.value}`.trim() : "";
        const note = typeof payload.note === "string" ? payload.note.trim() : "";
        const parts: string[] = [];
        if (scopes && scopes !== "-") {
            parts.push(scopes);
        } else if (value && value !== "-") {
            parts.push(`waarde ${value}`);
        }
        if (note && note !== "-") {
            parts.push(`notitie: ${note}`);
        }
        if (parts.length > 0) {
            return parts.join(" · ");
        }
    }
    if (typeof payload.text === "string" && payload.text.trim()) {
        return payload.text;
    }
    if ((eventType === "status_change" || eventType === "status_changed") && (payload.from || payload.to)) {
        return `${payload.from || "-"} -> ${payload.to || "-"}`;
    }
    if (eventType === "assignee_change") {
        return `Assignee gewijzigd van ${payload.from || "niet toegewezen"} naar ${payload.to || "niet toegewezen"}`;
    }
    if (eventType === "alert_triggered") {
        return `Alert trigger: waarde ${payload.value || "-"} (${payload.condition || "-"})`;
    }
    if (eventType === "created") {
        return "Aangemaakt";
    }
    if (eventType === "email_sent") {
        return `Email verzonden naar ${payload.to || "-"} met onderwerp '${payload.subject || "-"}'`;
    }
    if (eventType === "task_added" && payload.title) {
        const assigned = payload.assignedTo ? ` Toegewezen aan ${payload.assignedTo}.` : "";
        return `Taak "${payload.title}" toegevoegd.${assigned}`;
    }
    if (eventType === "comment_added" && payload.text) {
        return `${payload.text}`;
    }
    if (eventType === "task_assignee_change") {
        const title = payload.title || "Taak";
        const step = payload.stepName ? ` — ${payload.stepName}` : "";
        return `Taak "${title}"${step}: ${payload.from || "-"} → ${payload.to || "-"}`;
    }
    if (eventType === "task_updated") {
        const title = payload.title || "onbekende taak";
        const details: string[] = [];
        if (payload.titleFrom || payload.titleTo) {
            details.push(`titel "${payload.titleFrom || "-"}" -> "${payload.titleTo || "-"}"`);
        }
        if (payload.statusFrom || payload.statusTo) {
            details.push(`status ${payload.statusFrom || "-"} -> ${payload.statusTo || "-"}`);
        }
        if (payload.priorityFrom || payload.priorityTo) {
            details.push(`prioriteit ${payload.priorityFrom || "-"} -> ${payload.priorityTo || "-"}`);
        }
        if (payload.descriptionChanged === "true" || payload.descriptionChanged === true) {
            details.push("omschrijving aangepast");
        }
        if (details.length > 0) {
            return `Taak "${title}" bijgewerkt: ${details.join(", ")}`;
        }
        return `Taak "${title}" bijgewerkt`;
    }
    return null;
};

const isUserCommentEvent = (event: IDetailedActivityEvent): boolean => {
    const eventType = (event.eventType || "").toLowerCase();
    return eventType === "comment" || eventType === "message";
};

const canDeleteCommentEvent = <T extends IDetailedActivityEvent>(
    event: T,
    onDeleteComment?: (event: T) => Promise<void> | void,
    canEditComments?: boolean,
    currentUserUuid?: string
): boolean => {
    if (!onDeleteComment || !isUserCommentEvent(event)) {
        return false;
    }
    if (canEditComments) {
        return true;
    }
    return Boolean(currentUserUuid && event.createdBy && event.createdBy === currentUserUuid);
};

const DetailedActivityStream = <T extends IDetailedActivityEvent = IDetailedActivityEvent>({
    title = "Activiteit",
    events,
    loading = false,
    emptyMessage = "Nog geen activiteit",
    commentPlaceholder = "Voeg een opmerking toe...",
    commentValidation,
    maxHeight = "360px",
    onAddComment,
    onDeleteComment,
    canEditComments = false,
    currentUserUuid,
    detailResolver,
    eventLabelResolver,
    collapsibleComposer = false,
    addCommentButtonLabel = "Notitie toevoegen",
    showHeader = true,
}: DetailedActivityStreamProps<T>) => {
    const { t } = useTranslation();
    const [commentText, setCommentText] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [deletingCommentUuid, setDeletingCommentUuid] = useState<string | null>(null);
    const [composerOpen, setComposerOpen] = useState(!collapsibleComposer);
    const resolvedCommentValidation = commentValidation ?? COMMENT_FIELD_LIMITS.body;

    const sortedEvents = useMemo(() => {
        return [...(events || [])].sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return dateB - dateA;
        });
    }, [events]);

    const commentValid = isFieldValueValid(commentText, resolvedCommentValidation);

    const handleAddComment = () => {
        if (!onAddComment || !commentText.trim() || !commentValid) {
            return;
        }
        setSubmitting(true);
        Promise.resolve(onAddComment(commentText.trim()))
            .then(() => setCommentText(""))
            .finally(() => setSubmitting(false));
    };

    const handleDeleteComment = (event: React.MouseEvent<HTMLButtonElement>, activityEvent: T) => {
        if (!onDeleteComment || deletingCommentUuid) {
            return;
        }
        confirmPopup({
            target: event.currentTarget,
            message: t("workflow.comment.delete.confirm"),
            icon: "pi pi-info-circle",
            defaultFocus: "reject",
            acceptClassName: "p-button-danger",
            accept: () => {
                setDeletingCommentUuid(activityEvent.uuid);
                Promise.resolve(onDeleteComment(activityEvent))
                    .finally(() => setDeletingCommentUuid(null));
            },
        });
    };

    return (
        <div className="detailed-activity-stream">
            {showHeader && (
                <div className="detailed-activity-stream__header">
                    <h3>{title}</h3>
                    {onAddComment && collapsibleComposer && (
                        <Button
                            type="button"
                            className="detailed-activity-stream__add-button"
                            text
                            size="small"
                            icon={composerOpen ? "pi pi-times" : "pi pi-comment"}
                            label={composerOpen ? "Sluit" : addCommentButtonLabel}
                            onClick={() => setComposerOpen((previous) => !previous)}
                        />
                    )}
                </div>
            )}
            <div className="detailed-activity-stream__list" style={{ maxHeight }}>
                {loading && <p className="detailed-activity-stream__muted"><i className="pi pi-spinner pi-spin mr-2" />Laden...</p>}
                {!loading && sortedEvents.length === 0 && <p className="detailed-activity-stream__muted">{emptyMessage}</p>}
                {!loading && sortedEvents.map((event) => {
                    const detailText = detailResolver ? detailResolver(event) : resolveDetailedActivityEventDetail(event);
                    const showDelete = canDeleteCommentEvent(event, onDeleteComment, canEditComments, currentUserUuid);
                    return (
                        <div key={event.uuid} className="detailed-activity-stream__event">
                            {showDelete && (
                                <Button
                                    type="button"
                                    icon="pi pi-trash"
                                    text
                                    rounded
                                    size="small"
                                    className="detailed-activity-stream__delete-btn"
                                    aria-label={t("common.action.delete")}
                                    onClick={(clickEvent) => handleDeleteComment(clickEvent, event)}
                                    disabled={deletingCommentUuid === event.uuid}
                                    loading={deletingCommentUuid === event.uuid}
                                />
                            )}
                            <div className="detailed-activity-stream__event-row">
                                <div className="detailed-activity-stream__icon-wrap" title={event.contentTypeDescription || event.eventType}>
                                    <i className={`${defaultIconClass(event)} detailed-activity-stream__icon`} />
                                </div>
                                <div className="detailed-activity-stream__body">
                                    <p className="detailed-activity-stream__title">
                                        {eventLabelResolver ? eventLabelResolver(event) : resolveDetailedActivityEventLabel(event.eventType, t)}
                                    </p>
                                    {detailText && <p className="detailed-activity-stream__text">{detailText}</p>}
                                    <div className="detailed-activity-stream__meta">
                                        <span className="detailed-activity-stream__actor">Door: {event.actorName || "Onbekend"}</span>
                                        <span className="detailed-activity-stream__time">
                                            {event.createdAt ? new Date(event.createdAt).toLocaleString("nl-NL") : "-"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            {onAddComment && (!collapsibleComposer || composerOpen) && (
                <div className="detailed-activity-stream__composer">
                    <InputText
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder={commentPlaceholder}
                        maxLength={resolvedCommentValidation.maxLength}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddComment();
                            }
                        }}
                    />
                    <Button
                        type="button"
                        size="small"
                        className="primary-btn"
                        icon="pi pi-send"
                        onClick={handleAddComment}
                        disabled={!commentText.trim() || !commentValid || submitting}
                    />
                </div>
            )}
        </div>
    );
};

export default DetailedActivityStream;
